/**
 * ===========================================================================
 * NET — multiplayer client
 * ===========================================================================
 *
 * Turns the single-player game into a Call of Duty style team deathmatch
 * against other humans, talking to the authoritative Rust server in `server/`
 * over one WebSocket.
 *
 * ---------------------------------------------------------------------------
 * WHAT LIVES HERE
 *   socket.js   reconnecting WebSocket, JSON frames, no dependencies
 *   remote.js   one other player: snapshot ring, interpolation, puppet body
 *   hud.js      connection pill, scoreboard, death screen, chat
 *
 * ---------------------------------------------------------------------------
 * THE DEAL WITH AUTHORITY
 * ---------------------------------------------------------------------------
 * Movement is ours. We tell the server where we are 20 times a second and it
 * believes us within a speed and bounds check. Simulating this game's swept
 * capsule controller, mantle solver and BVH a second time in Rust would be a
 * rewrite of `src/physics`, and every mismatch between the two would surface as
 * rubber banding. Real CoD makes the same trade.
 *
 * Damage is not ours. We *detect* hits locally — our machine is the only one
 * holding the geometry that can answer "did that round land" — and report them.
 * The server checks the claim (alive, enemy, in range, near where the victim
 * actually was, inside a plausible fire rate) and owns the result. So the
 * hitmarker you see is a local prediction, and the kill is always the server's.
 *
 * Everyone else is drawn `interp_ms` in the past, between two snapshots we
 * already hold — see remote.js.
 *
 * ---------------------------------------------------------------------------
 * PUBLIC API — `const net = ctx.get('net')`
 * ---------------------------------------------------------------------------
 *   net.enabled        false in capture/deterministic runs and with ?mp=0
 *   net.connected      socket is up and we have been given an id
 *   net.id             our player id, 0 until the welcome lands
 *   net.players        Map<id, RemotePlayer> — everyone but us
 *   net.roster         last scoreboard the server sent
 *   net.ping           smoothed round trip, ms
 *   net.stats          { in, out, players, ping, drops }
 *   net.say(text)      push a chat line
 *   net.matchMapId     the map the SERVER is running (from Welcome.match.map)
 *   net.features       capabilities this server advertises, e.g. ['map_vote']
 *   net.voteMap(id)     cast a standing vote for the next map (see map.rs)
 *   net.onMapVote(h)    { onTally(votes), onResult(mapId) } for a live ballot
 *
 * CONTROLS added: Tab = scoreboard, Y = chat, Space = respawn when dead.
 *
 * ---------------------------------------------------------------------------
 * URL PARAMETERS
 *   ?mp=0                 disable multiplayer entirely
 *   ?server=ws://host/ws  point at a specific server (also remembered)
 *   ?name=GHOST           set the callsign (also remembered)
 *
 * Multiplayer is OFF in capture and deterministic runs, unconditionally: a
 * screenshot gate cannot depend on who else happens to be online.
 */

import * as THREE from 'three';
import { NetSocket } from './socket.js';
import { RemotePlayer } from './remote.js';
import { NetHud } from './hud.js';

/** Local state upload rate. Matches the server tick — more is wasted. */
const SEND_HZ = 20;
const PING_PERIOD = 2.0;
/** Cap on hits reported per second, so a bug here cannot look like a cheat. */
const HIT_BUDGET = 30;
const QUEUE_REQUIREMENTS = { '1v1': 2, '2v2': 4, TDM: 2, FFA: 4 };

const STANCE = { stand: 0, crouch: 1, prone: 2 };
const FLAG = { sprint: 1, ads: 2, airborne: 4, slide: 8, firing: 16, reload: 32 };

function defaultServerUrl() {
  const { protocol, hostname, host } = location;
  // Served from the deployed site: same origin, nginx proxies /mp to the game
  // server. Anywhere else: the dev server on its own port.
  if (protocol === 'https:') return `wss://${host}/mp`;
  if (hostname === 'localhost' || hostname === '127.0.0.1') return 'ws://127.0.0.1:7801/ws';
  return `ws://${hostname}:7801/ws`;
}

export class NetSystem {
  static id = 'net';
  static deps = ['player', 'ai', 'ui', 'physics', 'weapons'];

  async init(ctx) {
    this.ctx = ctx;
    this.rng = ctx.rng.fork();

    const params = new URLSearchParams(location.search);
    const capture = params.get('capture') === '1' || ctx.config.deterministic;
    this.enabled = !capture && params.get('mp') !== '0';

    this.id = 0;
    this.name = '';
    this.team = 0;
    this.connected = false;
    this.players = new Map();
    this.roster = [];
    this.teamScores = [0, 0];
    this.timeLeft = 0;
    this.mode = 'TEAM DEATHMATCH';
    this.ping = 0;
    this.alive = true;
    this.killedBy = '';
    this.respawnIn = 0;
    this.respawnsLeft = 5;
    this.maxRespawns = 5;
    this.spawnIndex = 0;
    this.stats = { in: 0, out: 0, players: 0, ping: 0, drops: 0, hits: 0, kills: 0 };
    /**
     * The map the SERVER is actually running, from `Welcome.match.map` — the
     * one this client's own `world.mapId` must match to be playing the same
     * level as everyone else. `null` until the first welcome lands.
     */
    this.matchMapId = null;
    /** `Welcome.features`, e.g. `['map_vote']` once a server supports it. */
    this.features = [];

    // ---- clocks -----------------------------------------------------------
    /** serverTime - localTime, smoothed. Set from the first snapshot. */
    this.clockOffset = null;
    this.interp = 0.1;
    this._sendAccum = 0;
    this._pingAccum = 0;
    this._pingSentAt = 0;
    this._hitWindow = 0;
    this._hitCount = 0;

    // ---- scratch (rule 5: nothing allocates per frame) ---------------------
    this._v = new THREE.Vector3();
    this._v2 = new THREE.Vector3();
    this._eye = new THREE.Vector3();
    this._statePayload = { t: 'state', seq: 0, p: [0, 0, 0], v: [0, 0, 0], yaw: 0, pitch: 0, st: 0, fl: 0 };
    this._firePayloadOut = { t: 'fire', o: [0, 0, 0], d: [0, 0, 0], w: 'rifle' };
    this._hitPayload = { t: 'hit', target: 0, dmg: 0, hs: false, p: [0, 0, 0] };
    this._pingPayload = { t: 'ping', ts: 0 };
    this._remoteFire = {
      weapon: 'rifle',
      origin: new THREE.Vector3(),
      dir: new THREE.Vector3(),
      seed: 0,
      remote: true,
      firstPerson: false,
    };
    this._tracer = { from: new THREE.Vector3(), to: new THREE.Vector3(), speed: 780, remote: true };
    this._reloadPayload = { weapon: 'rifle', phase: 'start', remote: true, position: new THREE.Vector3() };
    this._blips = [];
    this._seq = 0;
    // Connection is armed by the home screen, not by boot. This guarantees an
    // offline drill has no hidden remote opponents or background socket.
    this.sessionMode = 'idle';
    this.active = false;
    /** `null` = the server's persistent match; otherwise a matchmade/duel id. */
    this._connectedMatchId = null;

    if (!this.enabled) {
      console.info('[net] multiplayer disabled (capture run or ?mp=0)');
      return;
    }

    this.hud = new NetHud();
    this.hud.setStatus('warn', 'CONNECTING', '');
    this._wireEvents(ctx);
    this._wireChatKeys();

    // ---- identity ---------------------------------------------------------
    const wanted = params.get('name') ?? localStorage.getItem('cod:name') ?? '';
    this.name = (wanted || `OPERATOR${100 + Math.floor(this.rng.float() * 900)}`)
      .toUpperCase()
      .slice(0, 16);
    localStorage.setItem('cod:name', this.name);

    const url = params.get('server') ?? localStorage.getItem('cod:server') ?? defaultServerUrl();
    if (params.get('server')) localStorage.setItem('cod:server', url);
    this.url = url;

    this.socket = this._createSocket();
    console.info(`[net] multiplayer ready: ${this.name} -> ${url}`);
  }

  /**
   * Enable real-player networking only after ONLINE is explicitly selected.
   *
   * `matchId` is `null` for the server's one persistent match (unchanged,
   * default behaviour); non-null connects to a SPECIFIC match instead — one
   * `lobby` (`src/net/lobby.js`) just matchmade or duelled us into. See
   * `connectToMatch`, the thin wrapper `mainmenu.js` actually calls.
   */
  setSessionMode(session = 'offline', mode = 'TDM', matchId = null) {
    this.sessionMode = session === 'online' ? 'online' : 'offline';
    this.playlist = mode;
    this.active = this.sessionMode === 'online' && this.enabled;
    this.requiredPlayers = QUEUE_REQUIREMENTS[mode] ?? QUEUE_REQUIREMENTS.TDM;
    this.matchReady = false;

    if (this.active) {
      // An earlier queue may have been cancelled, which intentionally closes
      // its socket. A different match id also needs a fresh socket even if
      // the old one is still open — otherwise it keeps talking to whatever
      // match its URL last pointed at.
      if (this.socket?._stopped || matchId !== this._connectedMatchId) {
        this.socket?.close();
        this._connectedMatchId = matchId;
        this.socket = this._createSocket();
      }
      this.hud?.setStatus('warn', 'CONNECTING', 'REAL PLAYERS');
      this._emitQueue('CONNECTING', 'CONTACTING MULTIPLAYER SERVICE');
      this.socket?.connect();
      return true;
    }

    this.connected = false;
    for (const p of this.players.values()) p.dispose();
    this.players.clear();
    this.socket?.close();
    this.hud?.setStatus('ok', 'LOCAL DRILL', 'AI OPPONENTS');
    this._emitQueue('IDLE', 'LOCAL DRILL ACTIVE');
    return false;
  }

  /** Connect to a specific matchmade or duel match instead of the server's
   *  persistent one. `matchId` comes straight from `lobby:match-ready`. */
  connectToMatch(matchId, mode = 'TDM') {
    return this.setSessionMode('online', mode, matchId);
  }

  /* ---------------------------------------------------------- map vote -- */
  /**
   * Report this client's map vote to the lobby.
   *
   * Gated on the server advertising `map_vote` in its welcome, and that gate is
   * not politeness: `ClientMsg` on the server is a closed serde enum, so a tag it
   * does not know fails to deserialize rather than being ignored. Sending a vote
   * to a server that predates voting would cost the player their connection.
   *
   * Returns true if the vote actually went to the lobby. False means the pick is
   * local-only, which is the correct behaviour offline and against every server
   * shipped so far — the UI resolves the winner itself in that case.
   */
  voteMap(mapId) {
    this.localMapVote = mapId;
    if (!this.connected || !this.features?.includes('map_vote')) return false;
    this.socket?.send({ t: 'map_vote', map: mapId });
    return true;
  }

  /** Register lobby-vote handlers: `{ onTally, onResult }`. */
  onMapVote(handlers) {
    this.mapVoteHandlers = handlers ?? null;
    return () => {
      this.mapVoteHandlers = null;
    };
  }

  /** `this.url` with `?match=<id>` appended, for a matchmade/duel match. */
  _matchUrl(matchId) {
    if (!matchId) return this.url;
    try {
      const u = new URL(this.url, location.href);
      u.searchParams.set('match', matchId);
      return u.toString();
    } catch {
      return this.url;
    }
  }

  _createSocket() {
    return new NetSocket({
      url: this._matchUrl(this._connectedMatchId),
      onOpen: () => this._onOpen(),
      onClose: (why) => this._onClose(why),
      onMessage: (msg) => this._onMessage(msg),
    });
  }

  /** A truthful queue state for the homescreen; only network players count. */
  _emitQueue(status, detail) {
    if (!this.ctx) return;
    const players = this.connected ? this.players.size + 1 : 0;
    this.ctx.events.emit('net:queue', {
      status,
      players,
      required: this.requiredPlayers ?? 0,
      ready: !!this.matchReady,
      detail,
      mode: this.playlist ?? 'TDM',
    });
  }

  _refreshQueue() {
    if (!this.active) return;
    const players = this.players.size + (this.connected ? 1 : 0);
    const ready = this.connected && players >= (this.requiredPlayers ?? 2);
    this.matchReady = ready;
    this._emitQueue(
      ready ? 'MATCH READY' : 'SEARCHING',
      ready
        ? `${players} REAL PLAYERS VERIFIED`
        : `WAITING FOR ${Math.max(0, (this.requiredPlayers ?? 2) - players)} MORE REAL PLAYER${players + 1 < (this.requiredPlayers ?? 2) ? 'S' : ''}`
    );
  }

  /* ==================================================================== */
  /* engine events                                                        */
  /* ==================================================================== */

  _wireEvents(ctx) {
    this._off = [];
    const on = (t, fn) => this._off.push(ctx.events.on(t, fn));

    // Our own muzzle. `ai` fires with weapon 'ai_rifle' and our own remote
    // playback sets `remote`, so neither is echoed back to the server.
    on('weapon:fire', (e) => {
      if (!e || e.remote || !this.connected || !this.alive) return;
      const id = weaponId(e.weapon);
      if (id === 'ai_rifle') return;
      const o = this._firePayloadOut.o;
      const d = this._firePayloadOut.d;
      o[0] = e.origin.x; o[1] = e.origin.y; o[2] = e.origin.z;
      d[0] = e.dir.x; d[1] = e.dir.y; d[2] = e.dir.z;
      this._firePayloadOut.w = id;
      this.socket.send(this._firePayloadOut);
    });

    on('weapon:reload', (e) => {
      if (!e || e.remote || !this.connected) return;
      if ((e.phase ?? 'start') !== 'start') return;
      this.socket.send({ t: 'reload' });
    });

    // A round of ours landed on somebody. `ai` deliberately does not apply this
    // damage to a remote puppet (their health is the server's), so this is
    // purely the report — the hitmarker `ui` just drew is a prediction.
    on('damage:dealt', (e) => {
      const target = e?.target;
      if (!target?.isRemotePlayer || !this.connected || !this.alive) return;
      if (!this._spendHitBudget()) return;
      const p = this._hitPayload;
      p.target = target.netId;
      // A verified knife backstab is intentionally lethal against the 1000 HP
      // ruleset; the server independently checks weapon, distance and rear arc.
      p.dmg = Math.max(0, Math.min(1200, e.amount ?? 0));
      p.hs = !!e.headshot;
      if (e.point) {
        p.p[0] = e.point.x;
        p.p[1] = e.point.y;
        p.p[2] = e.point.z;
      } else {
        p.p[0] = p.p[1] = p.p[2] = 0;
      }
      this.stats.hits++;
      this.socket.send(p);
    });
  }

  /** Reporting more hits per second than any gun can fire means something is
   *  wrong locally; drop them rather than have the server ban us for it. */
  _spendHitBudget() {
    const now = this.ctx.time.raw;
    if (now - this._hitWindow >= 1) {
      this._hitWindow = now;
      this._hitCount = 0;
    }
    if (this._hitCount >= HIT_BUDGET) {
      this.stats.drops++;
      return false;
    }
    this._hitCount++;
    return true;
  }

  /* ==================================================================== */
  /* socket lifecycle                                                     */
  /* ==================================================================== */

  _onOpen() {
    if (!this.active) return;
    this.socket.send({ t: 'join', name: this.name, weapon: this._currentWeaponId() });
  }

  _onClose(why) {
    if (!this.active) return;
    this.connected = false;
    this.id = 0;
    for (const p of this.players.values()) p.dispose();
    this.players.clear();
    this.clockOffset = null;
    this.hud?.setStatus('bad', 'OFFLINE', why ? String(why).slice(0, 28) : '');
    this.matchReady = false;
    this._emitQueue('UNAVAILABLE', why ? `SERVICE UNAVAILABLE: ${String(why).slice(0, 48)}` : 'MULTIPLAYER SERVICE UNAVAILABLE');
    // A disconnect must not leave the player frozen on a death screen.
    if (!this.alive) this._localRespawn(this.spawnIndex);
  }

  _onMessage(msg) {
    if (!this.active) return;
    switch (msg?.t) {
      case 'welcome': return this._onWelcome(msg);
      case 'snapshot': return this._onSnapshot(msg);
      case 'spawned': return this._onSpawned(msg);
      case 'left': return this._onLeft(msg);
      case 'fire': return this._onRemoteFire(msg);
      case 'reload': return this._onRemoteReload(msg);
      case 'damage': return this._onDamage(msg);
      case 'confirm': return this._onConfirm(msg);
      case 'kill': return this._onKill(msg);
      case 'respawn': return this._onRespawn(msg);
      case 'score': return this._onScore(msg);
      case 'round_over': return this._onRoundOver(msg);
      case 'chat': return this._onChat(msg);
      // Lobby map vote. A server that never sends these simply leaves the vote
      // screen resolving locally; nothing here is required for a match to run.
      case 'map_tally': return this.mapVoteHandlers?.onTally?.(msg.votes ?? {});
      case 'map_pick':
        this.matchMapId = msg.map ?? this.matchMapId;
        return this.mapVoteHandlers?.onResult?.(msg.map);
      case 'pong': return this._onPong(msg);
      case 'error':
        console.warn('[net] server refused us:', msg.reason);
        this.hud?.setStatus('bad', 'REJECTED', String(msg.reason ?? '').slice(0, 24));
        this.matchReady = false;
        this._emitQueue('REJECTED', `SERVER REJECTED QUEUE: ${String(msg.reason ?? 'UNKNOWN').slice(0, 42)}`);
        return;
      default:
        return;
    }
  }

  _onWelcome(m) {
    this.id = m.id;
    this.name = m.name ?? this.name;
    this.team = m.team ?? 0;
    this.connected = true;
    /**
     * Optional capabilities the server advertises. `ClientMsg` on the server is a
     * closed enum, so a message type it does not know is a deserialization
     * failure, not a no-op — the client may only send an extension after the
     * server has said it understands it. Absent `features`, this is empty and
     * every extension below stays silent.
     */
    this.features = Array.isArray(m.features) ? m.features : [];
    // The map the SERVER is actually running. `world.mapId` (this build) and
    // `matchMapId` (the server's truth) can disagree — e.g. a vote resolved a
    // map change since this client last loaded — and reconciling that is the
    // caller's job (see `MainMenu`), not this system's; this only surfaces it.
    this.matchMapId = m.match?.map ?? this.matchMapId;
    this.interp = (m.interp_ms ?? 100) / 1000;
    this.teamScores = m.match?.team_scores ?? [0, 0];
    this.timeLeft = m.match?.time_left ?? 0;
    this.mode = m.match?.mode === 'TDM' ? 'TEAM DEATHMATCH' : (m.match?.mode ?? 'DEATHMATCH');
    this.maxRespawns = m.match?.max_respawns ?? 5;
    this.roster = m.players ?? [];
    this.spawnIndex = m.idx ?? 0;
    this.alive = true;

    // Start where the server says. It picks from the same spawn table
    // `src/world` owns, by index, so the client resolves the exact floor.
    this._localRespawn(this.spawnIndex);

    for (const info of this.roster) {
      if (info.id === this.id || this.players.has(info.id)) continue;
      this.players.set(info.id, new RemotePlayer(this, this.ctx, info, [0, -50, 0], 0));
    }
    const you = this.roster.find((p) => p.id === this.id);
    this.respawnsLeft = you?.respawns_left ?? this.maxRespawns;
    this._applySelfInfo(you);
    this.hud?.pushChat('', `CONNECTED AS ${this.name} — ${this.team === 0 ? 'BLUE' : 'ORANGE'} TEAM`, '#9fb3c8');
    if (you?.lifetime) {
      this.hud?.pushChat('', `LIFETIME KILLS: ${you.lifetime}`, '#9fb3c8');
    }
    console.info(`[net] welcome: id ${this.id}, team ${this.team}, ${this.roster.length} players`);
    this._refreshQueue();
  }

  _onSnapshot(m) {
    const localNow = this.ctx.time.raw;
    const off = m.st - localNow;
    // Track the offset with a fast rise and a slow fall: the smallest observed
    // offset is the one with the least queueing delay in it, so trust a new low
    // immediately and drift back up only slowly.
    if (this.clockOffset === null || off < this.clockOffset) this.clockOffset = off;
    else this.clockOffset += (off - this.clockOffset) * 0.02;

    for (const s of m.players) {
      if (s.id === this.id) {
        this._syncSelf(s);
        continue;
      }
      let rp = this.players.get(s.id);
      if (!rp) {
        rp = new RemotePlayer(
          this,
          this.ctx,
          { id: s.id, name: `PLAYER${s.id}`, team: s.tm, alive: s.al },
          s.p,
          s.y
        );
        this.players.set(s.id, rp);
      }
      rp.push(m.st, s);
    }
    this._refreshQueue();
  }

  /** The server's view of us. Health is theirs; position is ours. */
  _syncSelf(s) {
    const player = this.ctx.peek('player');
    const health = player?.health;
    if (!health || typeof health.value !== 'number') return;
    if (!this.alive || !s.al) return;
    const diff = s.hp - health.value;
    // Regen and rounding: follow the server. A drop of more than a point
    // arrives as a `damage` message instead, which plays the whole reaction.
    if (diff > 0.5) health.value = Math.min(health.max, s.hp);
    else if (diff < -0.5) health.value = Math.max(0, s.hp);
  }

  _onSpawned(m) {
    const info = m.player;
    if (!info || info.id === this.id) return;
    let rp = this.players.get(info.id);
    if (rp) {
      rp.name = info.name;
      rp.info = info;
      return;
    }
    rp = new RemotePlayer(this, this.ctx, info, m.p, m.yaw);
    this.players.set(info.id, rp);
    this.hud?.pushChat('', `${info.name} JOINED`, '#9fb3c8');
    this._refreshQueue();
  }

  _onLeft(m) {
    const rp = this.players.get(m.id);
    if (!rp) return;
    rp.dispose();
    this.players.delete(m.id);
    this.hud?.pushChat('', `${m.name || rp.name} LEFT`, '#9fb3c8');
    this._refreshQueue();
  }

  /**
   * Somebody else's muzzle. Replayed through the normal event vocabulary so
   * `fx` draws the flash, `audio` spatialises the report and `ai` is suppressed
   * by it — none of them need to know the round came off the wire.
   */
  _onRemoteFire(m) {
    const rp = this.players.get(m.id);
    const e = this._remoteFire;
    e.weapon = m.w || 'rifle';
    e.origin.set(m.o[0], m.o[1], m.o[2]);
    e.dir.set(m.d[0], m.d[1], m.d[2]);
    if (e.dir.lengthSq() < 1e-8) return;
    e.dir.normalize();
    e.seed = (this._seq = (this._seq + 1) >>> 0);
    this.ctx.events.emit('weapon:fire', e);
    if (rp?.agent?.animator?.recoil) {
      try {
        rp.agent.animator.recoil(1);
      } catch {
        /* animator has no recoil channel in this build */
      }
    }

    // Trace it for the tracer and the wall hit. The mask deliberately excludes
    // characters and ragdolls: `physics.fireBullet` emits `damage:dealt` for an
    // actor it strikes, and damage from a remote player's round is the server's
    // to decide, never ours.
    const phys = this.ctx.peek('physics');
    const t = this._tracer;
    t.from.copy(e.origin);
    if (phys) {
      const mask = phys.MASK.BULLET & ~phys.LAYER.ACTOR & ~phys.LAYER.RAGDOLL;
      const impacts = phys.fireBullet({
        origin: e.origin,
        dir: e.dir,
        damage: 26,
        penetration: 1,
        maxDist: 220,
        mask,
      });
      const last = impacts?.[impacts.length - 1];
      if (last?.point) t.to.copy(last.point);
      else t.to.copy(e.origin).addScaledVector(e.dir, 220);
    } else {
      t.to.copy(e.origin).addScaledVector(e.dir, 220);
    }
    this.ctx.events.emit('bullet:tracer', t);
  }

  _onRemoteReload(m) {
    const rp = this.players.get(m.id);
    if (!rp?.agent) return;
    try {
      rp.agent.animator?.reload?.(2.4);
    } catch {
      /* not fatal */
    }
    const p = this._reloadPayload;
    p.position.copy(rp.position);
    p.weapon = 'rifle';
    p.phase = 'start';
    this.ctx.events.emit('weapon:reload', p);
  }

  /** We were hit. The server has already decided how much. */
  _onDamage(m) {
    const player = this.ctx.peek('player');
    if (!player) return;
    const rp = this.players.get(m.from);
    const from = this._v;
    if (m.p && (m.p[0] || m.p[1] || m.p[2])) from.set(m.p[0], m.p[1], m.p[2]);
    else if (rp) from.copy(rp.position);
    else from.copy(player.position ?? this.ctx.camera.position);
    // If the attacker is on screen, point the damage arc at *them*, not at the
    // impact point on our own body.
    if (rp) from.copy(rp.position).setY(rp.position.y + 1.4);

    if ((m.hp ?? 1) <= 0 && this.alive && !this._pendingLethal) {
      this.respawnsLeft = Math.max(0, this.respawnsLeft - 1);
      this._pendingLethal = true;
    }
    player.applyDamage(m.dmg ?? 0, from, { type: 'bullet' });
    // Snap to the authoritative number: local falloff maths must never be the
    // reason two clients disagree about who is alive.
    const health = player.health;
    if (health && typeof health.value === 'number') health.value = Math.max(0, m.hp ?? health.value);
  }

  /** Our reported hit was accepted. `ui` already drew the marker; this is only
   *  interesting when it killed, because the kill row is the server's word. */
  _onConfirm(m) {
    if (!m.killed) return;
    const rp = this.players.get(m.target);
    if (rp) rp.health = 0;
  }

  _onKill(m) {
    this.teamScores = m.team_scores ?? this.teamScores;
    const ui = this.ctx.peek('ui');
    const mine = m.attacker === this.id;
    const victimIsMe = m.victim === this.id;

    // Drop the body. `ai` owns the ragdoll hand-off; we only say when.
    const rp = this.players.get(m.victim);
    if (rp) {
      const killer = this.players.get(m.attacker);
      const dir = this._v2.set(0, 0, 1);
      if (killer) dir.copy(rp.position).sub(killer.position).setY(0).normalize();
      else if (mine) dir.copy(rp.position).sub(this.ctx.camera.position).normalize();
      this._v.copy(rp.position);
      this._v.y += 1.25;
      rp.kill(this._v, dir);
    }

    ui?.killfeed?.push({
      attacker: m.attacker_name,
      victim: m.victim_name,
      headshot: !!m.hs,
      mine,
      attackerFriendly: mine,
    });

    if (mine) {
      this.stats.kills++;
      const player = this.ctx.peek('player');
      const xpGain = m.hs ? 150 : 100;
      if (!player?.progression?.authenticated) {
        // Guests still progress locally in PvP. Signed-in totals arrive from
        // the authoritative roster and must never be locally double-counted.
        player?.awardFightXp?.({ headshot: !!m.hs, online: false });
      }
      ui?.banner?.show(
        m.hs ? 'Headshot' : 'Enemy Eliminated',
        m.hs ? `+${xpGain} XP · HEADSHOT` : `+${xpGain} XP`
      );
    }
    if (victimIsMe) this._onLocalDeath(m.attacker_name);
  }

  _onLocalDeath(killer) {
    if (!this.alive) return;
    this.alive = false;
    this.killedBy = killer || 'ENEMY';
    if (!this._pendingLethal) this.respawnsLeft = Math.max(0, this.respawnsLeft - 1);
    this._pendingLethal = false;
    this.respawnIn = 5;
    const player = this.ctx.peek('player');
    player?.setControlEnabled?.(false);
    // The shared UI owns the terminal death screen for offline and online
    // matches alike. It offers a return home rather than a local respawn.
    this.hud?.hideDeath();
    this.ctx.events.emit('player:death', {
      position: this.ctx.camera.position,
      killer: this.killedBy,
      respawnsLeft: this.respawnsLeft,
      online: true,
    });
  }

  _onRespawn(m) {
    if (m.id === this.id) {
      this.spawnIndex = m.idx ?? this.spawnIndex;
      this._localRespawn(this.spawnIndex);
      return;
    }
    const rp = this.players.get(m.id);
    rp?.respawn(m.p, m.yaw);
  }

  _localRespawn(index) {
    const player = this.ctx.peek('player');
    this.alive = true;
    this.respawnIn = 0;
    this.hud?.hideDeath();
    if (!player) return;
    // `player.respawn(i)` reads the same spawn table the server picked from and
    // resolves the exact floor height through physics, so nobody spawns 4 cm
    // inside the pavement.
    try {
      player.respawn(index | 0);
    } catch (err) {
      console.warn('[net] respawn failed', err);
    }
    const ui = this.ctx.peek('ui');
    const roundAlreadyEnded = ui?.matchSession === 'online' && ui?.matchActive === false;
    player.setControlEnabled?.(!roundAlreadyEnded);
    const health = player.health;
    if (health?.reset) health.reset(true);
    // Reset ammo and grenades to starting values for this life
    const weapons = this.ctx.peek('weapons');
    weapons?.resetForRespawn?.();
    if (!roundAlreadyEnded) {
      this.ctx.events.emit('player:respawn', { online: true, respawnsLeft: this.respawnsLeft });
    }
  }

  _onScore(m) {
    this.roster = m.players ?? this.roster;
    this.teamScores = m.team_scores ?? this.teamScores;
    this.timeLeft = m.time_left ?? this.timeLeft;
    const mine = this.roster.find((p) => p.id === this.id);
    if (mine?.respawns_left !== undefined) this.respawnsLeft = mine.respawns_left;
    // Names arrive with the roster; a puppet built from a bare snapshot gets
    // its real callsign here.
    for (const info of this.roster) {
      const rp = this.players.get(info.id);
      if (!rp) continue;
      rp.info = info;
      if (info.name && rp.name !== info.name) {
        rp.name = info.name;
        if (rp.agent) rp.agent.name = info.name;
      }
    }
    this._applySelfInfo(this.roster.find((p) => p.id === this.id));
  }

  /** The server's word on our own level/XP — pushed to `player` so the menu's
   *  service record reflects it. Reached only from roster data the server
   *  sent us (`welcome`/`score`/`round_over`), which is itself only ever
   *  incremented for a validated real-player kill (see `Game::kill` on the
   *  server) — offline bot kills never touch this. */
  _applySelfInfo(info) {
    if (!info) return;
    const player = this.ctx.peek('player');
    if (!player) return;
    // A signed-in account uses the server total across offline and online
    // fights. An anonymous socket starts at zero server-side, so do not erase
    // the guest's device-local progression merely because they joined PvP.
    if (typeof info.xp === 'number' && (player.progression?.authenticated || info.xp > (player.xp ?? 0))) {
      player.setAuthoritativeXp?.(info.xp, player.progression?.authenticated);
    }
  }

  _onRoundOver(m) {
    const finalScores = m.team_scores ?? this.teamScores;
    const won = m.winner === this.team;
    const ui = this.ctx.peek('ui');
    ui?.banner?.show(
      m.winner === 2 ? 'Draw' : won ? 'Victory' : 'Defeat',
      `${m.team_scores?.[0] ?? 0} — ${m.team_scores?.[1] ?? 0}`,
      6
    );
    this.hud?.pushChat('', 'ROUND OVER', '#ffc247');
    this._applySelfInfo((m.players ?? []).find((p) => p.id === this.id));
    const coins = m.coin_rewards?.[this.id] ?? 0;
    this.ctx.events.emit('match:round-over', {
      result: m.winner === 2 ? 'DRAW' : won ? 'VICTORY' : 'DEFEAT',
      scoreUs: finalScores[this.team & 1] ?? 0,
      scoreThem: finalScores[1 - (this.team & 1)] ?? 0,
      coins,
    });
    this.teamScores = [0, 0];
  }

  _onChat(m) {
    if (!m.text) return;
    this.hud?.pushChat(m.name ?? '', m.text, m.id === this.id ? '#ffc247' : '#5fb0ff');
  }

  _onPong(m) {
    const rtt = (this.ctx.time.raw - (m.ts ?? 0)) * 1000;
    if (rtt >= 0 && rtt < 5000) this.ping = this.ping ? this.ping * 0.7 + rtt * 0.3 : rtt;
  }

  /* ==================================================================== */
  /* frame                                                                */
  /* ==================================================================== */

  update(dt, ctx) {
    if (!this.enabled || !this.active) return;
    this.socket.update(dt);
    this.hud.update(dt);

    if (this.connected) {
      this._sendAccum += dt;
      const period = 1 / SEND_HZ;
      if (this._sendAccum >= period) {
        this._sendAccum %= period;
        this._sendState();
      }
      this._pingAccum += dt;
      if (this._pingAccum >= PING_PERIOD) {
        this._pingAccum = 0;
        // `ts` carries our own clock straight back in the pong: the server
        // never interprets it, so the round trip needs no clock agreement.
        this._pingPayload.ts = ctx.time.raw;
        this.socket.send(this._pingPayload);
      }
    }

    // Everyone else, played back in the past.
    const renderTime = this.clockOffset === null ? 0 : ctx.time.raw + this.clockOffset - this.interp;
    if (this.clockOffset !== null) {
      for (const rp of this.players.values()) rp.update(dt, renderTime);
    }

    this._updateDeath(dt);
    this._updateInput();
    this._updateHud();
  }

  lateUpdate() {
    if (!this.enabled || !this.active || !this.connected) return;
    this.hud?.drawNameplates?.(
      this.players,
      this.ctx.camera,
      this.ctx.canvas.clientWidth || innerWidth,
      this.ctx.canvas.clientHeight || innerHeight
    );
    // Contacts on the minimap. `ui` only computes its own blips when `ai`
    // exposes getHudActors(), which it does not, so this is the whole radar.
    const list = this._blips;
    list.length = 0;
    for (const rp of this.players.values()) {
      if (!rp.alive || !rp.agent) continue;
      list.push({
        x: rp.position.x,
        z: rp.position.z,
        kind: rp.team === this.team ? 'friend' : 'enemy',
        heading: (rp.yaw * 180) / Math.PI,
      });
    }
    const ui = this.ctx.peek('ui');
    ui?.setBlips?.(list);
    ui?.setMatch?.({
      scoreUs: this.teamScores[this.team & 1] ?? 0,
      scoreThem: this.teamScores[1 - (this.team & 1)] ?? 0,
      timeLeft: this.timeLeft,
      mode: this.playlist ?? 'TDM',
    });
  }

  _sendState() {
    const player = this.ctx.peek('player');
    if (!player?.position) return;
    const s = this._statePayload;
    const p = player.position;
    s.seq = ++this._seq;
    s.p[0] = p.x; s.p[1] = p.y; s.p[2] = p.z;
    const v = player.velocity;
    if (v) { s.v[0] = v.x; s.v[1] = v.y; s.v[2] = v.z; }
    s.yaw = player.yaw ?? 0;
    s.pitch = player.pitch ?? 0;
    s.st = STANCE[player.stance] ?? 0;
    let fl = 0;
    if (player.sprinting) fl |= FLAG.sprint;
    if (player.adsProgress > 0.5) fl |= FLAG.ads;
    if (player.airborne) fl |= FLAG.airborne;
    if (player.sliding) fl |= FLAG.slide;
    const weapons = this.ctx.peek('weapons');
    if (weapons?.firing) fl |= FLAG.firing;
    if (weapons?.reloading) fl |= FLAG.reload;
    s.fl = fl;
    this.socket.send(s);
    this.stats.out = this.socket.bytesOut;
    this.stats.in = this.socket.bytesIn;
  }

  _updateDeath(dt) {
    if (!this.alive) this.respawnIn = Math.max(0, this.respawnIn - dt);
  }

  _updateInput() {
    const input = this.ctx.input;
    if (this.chatOpen) return;
    this.hud.setBoardVisible(input.held('Tab'));
    if (input.pressed('KeyY')) this._openChat();
  }

  _updateHud() {
    const status = this.socket.status;
    if (status === 'open' && this.connected) {
      const n = this.players.size + 1;
      this.hud.setStatus(
        this.ping > 180 ? 'warn' : 'ok',
        `${n} ${n === 1 ? 'OPERATOR' : 'OPERATORS'}`,
        `${Math.round(this.ping)}MS`
      );
    } else if (status === 'connecting') {
      this.hud.setStatus('warn', 'CONNECTING', '');
    } else {
      this.hud.setStatus('bad', 'OFFLINE', this.socket.lastError.slice(0, 26));
    }
    this.stats.players = this.players.size;
    this.stats.ping = Math.round(this.ping);
    if (this.hud.board.classList.contains('on')) {
      this.hud.drawBoard(this.roster, this.teamScores, this.timeLeft, this.id, this.mode);
    }
    if (this.timeLeft > 0) this.timeLeft = Math.max(0, this.timeLeft - this.ctx.time.dt);
  }

  /* ==================================================================== */
  /* chat                                                                 */
  /* ==================================================================== */

  _wireChatKeys() {
    this.chatOpen = false;
    this.chatText = '';
    this._onKeyDown = (e) => {
      if (!this.chatOpen) return;
      e.preventDefault();
      e.stopPropagation();
      if (e.key === 'Escape') return this._closeChat(false);
      if (e.key === 'Enter') return this._closeChat(true);
      if (e.key === 'Backspace') {
        this.chatText = this.chatText.slice(0, -1);
      } else if (e.key.length === 1 && this.chatText.length < 120) {
        this.chatText += e.key;
      }
      this.hud.setInput(true, this.chatText);
    };
    addEventListener('keydown', this._onKeyDown, true);
  }

  _openChat() {
    if (this.chatOpen) return;
    this.chatOpen = true;
    this.chatText = '';
    // Release every held key before muting input, or a held W keeps running.
    dispatchEvent(new Event('blur'));
    this.ctx.input.enabled = false;
    this.hud.setInput(true, '');
  }

  _closeChat(send) {
    this.chatOpen = false;
    this.ctx.input.enabled = true;
    this.hud.setInput(false, '');
    const text = this.chatText.trim();
    this.chatText = '';
    if (send && text) this.say(text);
  }

  say(text) {
    if (!this.connected) return;
    this.socket.queue({ t: 'chat', text: String(text).slice(0, 160) });
  }

  /* ==================================================================== */

  _currentWeaponId() {
    return weaponId(this.ctx.peek('weapons')?.current) || 'rifle';
  }

  dispose() {
    for (const off of this._off ?? []) off();
    this._off = null;
    if (this._onKeyDown) removeEventListener('keydown', this._onKeyDown, true);
    for (const p of this.players.values()) p.dispose();
    this.players.clear();
    this.socket?.close();
    this.hud?.dispose();
  }
}

function weaponId(w) {
  if (!w) return 'rifle';
  if (typeof w === 'string') return w;
  return w.id ?? w.name ?? 'rifle';
}
